<template>
    <v-card>
        <v-card-title class="headline mb-1">
            <slot name="title">
                {{ title }}
            </slot>
        </v-card-title>

        <v-card-text>
            <slot name="before-inputs" />

            <rc-base-input
                v-if="allow_label_edit"
                label="Name"
                v-model="bulkMessageLabel"
                hint='Use a descriptive name that includes the month and year, e.g. "December 2023 Credit Card Declines"'
                autofocus
                required
            />

            <p class="mb-2">
                <rc-link
                    href="https://redchirp.com/who-can-i-send-promotional-text-messages-to/"
                    text="Help Article: Who can I send promotional messages to?"
                />
            </p>

            <p>
                <rc-link
                    href="https://redchirp.com/who-can-i-send-informational-text-messages-to/"
                    text="Help Article: Who can I send informational messages to?"
                />
            </p>

            <rc-base-input
                type="select"
                label="Message Type"
                :options="message_category_choices"
                option-label="text"
                hint="See the help articles above for more information on message types"
                v-model="bulkMessageCategory"
                required
            />

            <rc-base-input
                class="mt-6"
                type="select"
                label="Filter: Try to only Send to Recipients When..."
                :options="$rc.enumChoices('create_bulk_message_filter_type_only_send_to_recipients_when', false)"
                html-options
                option-label="html"
                v-model="bulkMessageFilter"
                dropdown-placement="top"
                :error="has_order_history_error"
                :error-message="order_history_error_text"
                required
            />

            <slot name="after-inputs" />
        </v-card-text>

        <v-card-actions class="justify-space-between flex-wrap gap-2">
            <slot name="actions-left">
                <span></span>
            </slot>

            <div class="d-flex gap-2">
                <v-btn
                    text
                    @click="$emit('cancel')"
                >
                    Cancel
                </v-btn>

                <v-btn
                    color="primary"
                    :disabled="!isValid()"
                    @click="$emit('ok')"
                >
                    {{ okText }}
                </v-btn>
            </div>
        </v-card-actions>
    </v-card>
</template>

<script>
export default {
    props: {
        allow_label_edit: {
            type: Boolean,
            default: true,
        },

        bulk_message_label: {
            type: String,
            default: '',
        },

        bulk_message_category: {
            type: String,
            default: '',
        },

        bulk_message_filter: {
            type: String,
            default: '',
        },

        title: {
            type: String,
            default: 'Create New Bulk Message',
        },

        okText: {
            type: String,
            default: 'Ok',
        },
    },

    data() {
        return {
            creation_profile: null,
            message_category_choices: [
                { text: 'Promotional', value: 'promotional' },
                { text: 'Informational', value: 'informational' },
            ],
        }
    },

    computed: {
        bulkMessageLabel: {
            get() {
                return this.bulk_message_label;
            },
            set(value) {
                this.$emit('update:bulk_message_label', value);
            }
        },

        bulkMessageCategory: {
            get() {
                return this.bulk_message_category;
            },
            set(value) {
                this.$emit('update:bulk_message_category', value);
            }
        },

        bulkMessageFilter: {
            get() {
                return this.bulk_message_filter;
            },
            set(value) {
                this.$emit('update:bulk_message_filter', value);
            }
        },

        order_history_error_text() {
            if (!this.has_order_history_error) return null;
            return "It looks like you don't currently have an active CRM/POS integration that supports order history filtering. This means you'll need to manually filter your recipients, since RedChirp cannot automatically handle this step for you."
        },

        has_order_history_error() {
            if (this.creation_profile?.is_order_history_filter_available) return false;
            return this.has_selected_order_history_filter_that_requires_crm;
        },

        has_selected_order_history_filter_that_requires_crm() {
            return !['', 'consent-on-file', 'other'].includes(this.bulk_message_filter);
        },

        order_history_filter_value_for_stub() {
            if (!this.has_selected_order_history_filter_that_requires_crm) {
                return null;
            }
            return this.bulk_message_filter;
        },
    },

    methods: {
        getCreationProfile() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'GET: /api/v1/bulk/create/get-create-bulk-message-profile/:organization_id',

                params: {
                    organization_id: this.$rc.getOrganizationId(),
                },

                on_success: (data) => {
                    this.creation_profile = data;
                },

                on_error: () => {
                    this.creation_profile = null;
                },
            })
        },

        isValid() {
            if (this.allow_label_edit && (this.bulk_message_label == null || this.bulk_message_label == '' || this.bulk_message_label.trim() == '')) return false;
            if (this.bulk_message_category == null || this.bulk_message_category == '') return false;
            if (!this.bulk_message_filter || this.has_order_history_error) return false;
            return true;
        },
    },

    mounted() {
        this.getCreationProfile();
    },
}
</script>

<style lang="scss" scoped>
.v-btn {
    white-space: normal;
    flex-shrink: 1;

    :deep(.v-btn__content) {
        flex-shrink: 1;
    }
}
</style>